import React, { useEffect, useState } from "react";
import { object, shape, string, arrayOf } from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { collectionToStoriesWithForumFilter } from "../../../utils/forum-filter";
import HeroBanner from "../HeroBanner";
import CollectionSummary from "../../molecules/CollectionSummary";
import EventsListingSection from "../EventsListingSection";

import { mapCollectionStoryToHeroData, fetchEventsCollection } from "../../pages/events-page/events-page-mapper";
import { RESEARCH_HUB_CONTENT } from "../../../constants";
import LoadingIndicatorComponent from "../../atoms/loading-indicator";

import "./events-landing-page.m.css";

const EVENT_TEMPLATES = ["conference", "workshop", "session"];

export const EventsLandingPage = ({
  data,
  eventType,
  className,
  isMobile,
  showEventName,
  // title is extracted from props to prevent it being spread as an HTML attribute
  // on the root div, which would cause a native browser tooltip to appear on hover
  title: _title,
  ...props
}) => {
  const collection = get(data, ['collection']);
  
  const [heroData, setHeroData] = useState(() => {
    const items = collection?.items || [];
    const eventStories = items?.filter(item => EVENT_TEMPLATES.includes(item?.story?.["story-template"]));
    const heroStory = eventStories[0] || null;
    
    if (heroStory && collection) {
      return mapCollectionStoryToHeroData(heroStory, collection);
    }
    return null;
  });

  useEffect(() => {
    if (heroData) return;

    const loadHeroData = async () => {
      const { heroStory, collectionData } = await fetchEventsCollection(eventType);
      const mappedHeroData = mapCollectionStoryToHeroData(heroStory, collectionData);
      setHeroData(mappedHeroData);
    };

    loadHeroData();
  }, [eventType, collection]);

  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));

  // Show loading while waiting for subscriber metadata for logged-in users
  if (member && !subscriberMetadata) {
    return <LoadingIndicatorComponent />;
  }

  const upcomingCollection = collection?.items?.find(item => item.slug === `upcoming-${eventType}`);
  const previousCollection = collection?.items?.find(item => item.slug === `previous-${eventType}`);

  const upcomingEvents = upcomingCollection ? collectionToStoriesWithForumFilter(upcomingCollection, member, subscriberMetadata) : [];
  const previousEvents = previousCollection ? collectionToStoriesWithForumFilter(previousCollection, member, subscriberMetadata) : [];

  const collectionTitle = collection?.name || "";
  const collectionSummary = collection?.summary || "";

  if (upcomingEvents.length === 0 && previousEvents.length === 0) {
    return (
      <div styleName="events-landing-page" className={className} {...props}>
        <h1 style={{ textAlign: "center", marginTop: "50px" }}>{RESEARCH_HUB_CONTENT.NO_CONTENT_MESSAGE}</h1>
      </div>
    );
  }

  const { breadcrumbs = [], title = "", eventInfo = {}, buttons = [], storyUrl } = heroData || {};

  return (
    <div styleName="events-landing-page" className={className} {...props}>
      <HeroBanner
        backgroundImage={collection?.metadata?.["cover-image"]?.url}
        breadcrumbs={breadcrumbs}
        title={title}
        eventInfo={eventInfo}
        buttons={buttons}
        storyUrl={storyUrl}
      />

      <CollectionSummary
        title={collectionTitle}
        summary={collectionSummary}
      />

      <EventsListingSection
        stories={upcomingEvents}
        backgroundImage={upcomingCollection?.metadata?.["cover-image"]?.url || upcomingCollection?.metadata?.["cover-image"]?.["cover-image-url"]}
        eventType={eventType}
        variant="upcoming"
        isMobile={isMobile}
        showEventName={showEventName}
      />

      <EventsListingSection
        stories={previousEvents}
        eventType={eventType}
        variant="previous"
        isMobile={isMobile}
        showEventName={showEventName}
      />
    </div>
  );
};

EventsLandingPage.propTypes = {
  data: shape({
    collection: shape({
      name: string,
      summary: string,
      "hero-image-s3-key": string,
      "hero-image-metadata": object
    })
  }),
  eventType: string.isRequired,
  className: string
};

export default EventsLandingPage;